import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import Chart from 'chart.js/auto';
import {
    DashboardKeyMetricsService,
    DashboardMainPageGraphs
} from '@services/dashboard-key-metrics.service';
import { Subject, takeUntil } from 'rxjs';
import { PlacementService } from '@services/placement.service';

@Component({
    selector: 'app-graph-item',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './graph-item.component.html',
    styleUrl: './graph-item.component.scss'
})
export class GraphItemComponent implements OnInit, OnChanges, OnDestroy {
    @Input() farmId: number | null = null;
    chickGrowthGraph: Chart | null = null;
    isLoading: boolean = false;
    private destroy$ = new Subject<void>();
    farmCycles: any[] = [];
    selectedCycleId!: number;

    private isInitialLoad = true;

    constructor(private dashboardService: DashboardKeyMetricsService, private placementService: PlacementService) { }

    ngOnInit() {
        if (this.farmId) {
            // this.loadDashboardGraphs();
            this.getAllFarmCycle();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['farmId'] && !changes['farmId'].firstChange) {
            this.getAllFarmCycle();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.chickGrowthGraph?.destroy();
    }



    private getAllFarmCycle() {
        if (this.farmId == null) return;

        this.placementService
            .getCyclesByFarmAndHouse(this.farmId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response: any) => {
                const data = response as any[];

                this.farmCycles = data.map(c => ({
                    id: c.id ?? c.Id,
                    name: c.name ?? c.Name
                }));

                if (this.farmCycles.length > 0) {
                    this.selectedCycleId = this.farmCycles[0].id;
                    this.loadDashboardGraphs();
                }

                console.log(this.farmCycles);
            }, err => {
                console.error(err);
            });
    }

    onCycleChange(event: Event): void {
        if (this.isInitialLoad) {
            this.isInitialLoad = false;
            return;
        }
        const selectElement = event.target as HTMLSelectElement;
        this.selectedCycleId = Number(selectElement.value);

        console.log('Selected Cycle ID:', this.selectedCycleId);

        this.loadDashboardGraphs();
    }

    private loadDashboardGraphs(): void {
        if (this.farmId == null) return;

         if (this.selectedCycleId == null)
        return;
    
        this.isLoading = true;

        this.dashboardService
            .getDashboardMainPageGraphs(this.farmId, this.selectedCycleId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: data => {
                    this.isLoading = false;
                    this.renderCharts(data);
                },
                error: () => {
                    this.isLoading = false;
                    this.renderCharts(undefined);
                }
            });
    }

    private renderCharts(data?: DashboardMainPageGraphs) {

        const dailyEntries = (data?.chickGrowth || []).filter(
            (cg) => cg.farmId === this.farmId
        );

        const actualWeights: number[] = [];
        const predictedValues: number[] = [];

        // Extract actual and predicted values
        dailyEntries.forEach((entry) => {

            const arr = entry.houseWeights || [];

            arr.forEach((house) => {

                const weight = house?.avgWeight || 0;

                if (house?.isPredicted) {
                    predictedValues.push(weight);
                }
                else {
                    actualWeights.push(weight);
                }

            });

        });

        const actualWeeks = actualWeights.length;

        // first predicted value → 7-day prediction
        const firstPrediction =
            predictedValues.length > 0 ? predictedValues[0] : null;

        // last predicted value → final prediction
        const finalPrediction =
            predictedValues.length > 0
                ? predictedValues[predictedValues.length - 1]
                : null;

        let predictionWeek = actualWeeks + 1;
        let finalWeek = 5;

        // If cycle already passed week 5
        if (actualWeeks >= 5) {
            predictionWeek = actualWeeks + 1;
            finalWeek = actualWeeks + 2;
        }

        const totalWeeks = Math.max(finalWeek, predictionWeek);

        const labels: string[] = [];
        const actualData: (number | null)[] = [];
        const predictedData: (number | null)[] = [];

        for (let i = 1; i <= totalWeeks; i++) {

            // labels.push(`Week ${i}`);

            if (i === 1) 
                {
                   labels.push('ODO');
                } 
            else {
                    labels.push(`Week ${i - 1}`);
                }

            // Actual values
            if (i <= actualWeeks) {
                actualData.push(actualWeights[i - 1]);
            }
            else {
                actualData.push(null);
            }

            // Prediction values
            if (i === predictionWeek) {
                predictedData.push(firstPrediction);
            }
            else if (i === finalWeek) {
                predictedData.push(finalPrediction);
            }
            else {
                predictedData.push(null);
            }

        }

        if (this.chickGrowthGraph) {

            this.chickGrowthGraph.data.labels = labels as any;
            this.chickGrowthGraph.data.datasets[0].data = actualData as any;
            this.chickGrowthGraph.data.datasets[1].data = predictedData as any;

            this.chickGrowthGraph.update();
        }
        else {

            this.chickGrowthGraph = new Chart('chickGrowthGraph', {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Actual Weight',
                            data: actualData,
                            borderColor: 'rgba(0, 255, 255, 1)',
                            backgroundColor: 'rgba(83, 255, 240, 0.3)',
                            pointBackgroundColor: 'rgba(83, 255, 240, 1)',
                            pointRadius: 6,
                            borderWidth: 2,
                            spanGaps: false
                        },
                        {
                            label: 'Predicted Weight',
                            data: predictedData,
                            borderColor: 'rgba(255, 193, 7, 1)',
                            backgroundColor: 'rgba(255, 193, 7, 0.2)',
                            pointBackgroundColor: 'rgba(255, 193, 7, 1)',
                            pointRadius: 6,
                            borderWidth: 2,
                            borderDash: [5, 5],
                            spanGaps: true
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Chick Growth Performance',
                            color: '#fff',
                            font: { size: 16 }
                        },
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                color: '#fff',
                                usePointStyle: true
                            }
                        }
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: 'Age (Weeks)',
                                color: '#fff'
                            },
                            ticks: { color: '#fff' },
                            grid: { color: 'rgba(255,255,255,0.2)' }
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Weight (g)',
                                color: '#fff'
                            },
                            ticks: { color: '#fff' },
                            grid: { color: 'rgba(255,255,255,0.2)' }
                        }
                    }
                }
            });

        }
    }
}
